import cx from 'classnames';
import {cloneDeep, get, isEmpty, isFunction, map, min} from 'lodash';
import {useCallback, useEffect, useMemo, useState} from 'react';

import {useIsChanged} from 'hooks';
import {NodePath} from 'types';

import {Icon} from '../Icon';

import {DEFAULT_TREE_CONFIG, MAX_DEPTH, TREE_CONFIG} from './config';
import {TreeNode, TreeNodeState, TreeRenderProps, TreeValidateProps} from './types';

type Props = {
    node: TreeNode<any>;
    editable?: boolean;
    removable?: boolean;
    render: (props: TreeRenderProps<any>) => React.ReactNode;
    validate?: (props: TreeValidateProps<any>) => boolean;
    canAddChild?: (node: TreeNode<any>) => boolean;
    level?: number;
    path?: NodePath;
    maxDepth?: number;
    nodeState: TreeNodeState;
    updateNodeState: (path: NodePath, partialState: Partial<TreeNodeState>) => void;
    onRemove?: (path: NodePath) => void;
    onAdd?: (path: NodePath) => void;
    onNodeValueChange?: (value: any, path: NodePath) => void;
}

export const Tree: React.FC<Props> = ({
    node, render, level = 0, maxDepth: maxDepthProp = MAX_DEPTH, nodeState, updateNodeState,
    path = [0], editable, removable, onNodeValueChange, onRemove, onAdd, canAddChild, validate
}) => {
    const config = useMemo(() => TREE_CONFIG[level] || DEFAULT_TREE_CONFIG, [level]);
    const expanded = useMemo(() => get(nodeState, path)?.expanded, [nodeState, path]);
    const hasChildren = useMemo(() => !isEmpty(node.children), [node]);
    const iconName = useMemo(() => !expanded ? 'chevron-right' : 'chevron-down', [expanded]);
    const maxDepth = useMemo(() => min([maxDepthProp, MAX_DEPTH]), [maxDepthProp]);
    const maxDepthExceeded = useMemo(() => level >= maxDepth, [level, maxDepth]);
    const [localValue, setLocalValue] = useState(null);
    const isEdit = useMemo(() => get(nodeState, path)?.edit, [path, nodeState]);
    const isValid = useMemo(() => !get(nodeState, path)?.error, [path, nodeState]);
    const isLocalValid = useMemo(() => isFunction(validate) ?
        validate({value: localValue || node.value, children: localValue ? [] : node.children, isEdit, expanded, level}) : true,
    [validate, node, localValue, isEdit, expanded, level]);
    const validChanged = useIsChanged(isLocalValid);
    const showAddChildBtn = useMemo(() => editable && canAddChild(node), [editable, node, canAddChild]);

    const handleEdit = useCallback(() => {
        setLocalValue(cloneDeep(node.value));
        updateNodeState(path, {edit: true});
    }, [node, path, updateNodeState]);

    const handleNodeValueChange = useCallback((value: any) => {
        setLocalValue((prev: any) => ({...prev || node.value, ...value}));
    }, [node]);

    const handleCancel = useCallback(() => {
        updateNodeState(path, {edit: false});
        setLocalValue(null);
    }, [path, updateNodeState]);

    const handleSave = useCallback(() => {
        if (isValid) {
            onNodeValueChange(localValue, path);
            handleCancel();
        }
    }, [isValid, path, onNodeValueChange, localValue, handleCancel]);

    useEffect(() => {
        if (validChanged) {
            updateNodeState(path, {error: !isLocalValid});
        }
    }, [isLocalValid, path, updateNodeState, validChanged]);

    if (!node) {
        return null;
    }

    return (
        <div className={`tree-level-${level}`}>
            <div className={`tw-flex tw-items-center ${config.className}`}>
                <div className='tw-min-w-6 md:tw-min-w-8'>
                    {hasChildren &&
                        <Icon
                            fill='currentColor'
                            name={iconName}
                            button
                            group
                            onClick={() => updateNodeState(path, {expanded: !expanded})}
                        />}
                </div>
                <div
                    style={{borderLeftColor: config.color}}
                    className={cx(
                        'tw-flex tw-items-center tw-px-2 md:tw-px-6 tw-py-2 md:tw-py-4 tw-my-2',
                        'tw-rounded tw-border tw-border-l-6 tw-grow',
                        {'tw-border-alert': !isValid && !isEdit, 'tw-border-default': isValid})}
                >
                    <div className='tw-grow'>
                        {render({
                            value: localValue || node.value,
                            isEdit,
                            expanded,
                            path,
                            onNodeValueChange: handleNodeValueChange
                        })}
                    </div>
                    <div className='tw-flex tw-items-center tw-ml-2'>
                        {editable && !isEdit && <Icon name='edit' size='sm' button group onClick={handleEdit} />}
                        {editable && isEdit && <Icon name='check' disabled={!isValid} color='success' button group onClick={handleSave} />}
                        {editable && isEdit && <Icon name='close' button group onClick={handleCancel} />}
                        {showAddChildBtn && <Icon name='plus' color='primary' button group onClick={() => onAdd(path)} />}
                        {removable && !isEdit && <Icon name='delete' color='alert' size='sm' button group onClick={() => onRemove(path)} />}
                    </div>
                </div>
            </div>

            {hasChildren && expanded && !maxDepthExceeded && map(node.children, (child, i) => (
                <Tree
                    key={`node-${[...path, i].join('.')}`}
                    node={child}
                    editable={editable}
                    removable={removable}
                    level={level + 1}
                    maxDepth={maxDepth}
                    path={[...path, i]}
                    render={render}
                    validate={validate}
                    nodeState={nodeState}
                    updateNodeState={updateNodeState}
                    canAddChild={canAddChild}
                    onRemove={onRemove}
                    onNodeValueChange={onNodeValueChange}
                    onAdd={onAdd}
                />
            ))}
        </div>
    );
};